import { ChangeDetectionStrategy, Component, computed, model, signal } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { NOTE_COLOR_SWATCHES } from '@/app/support/support-markup';
import { HdDotPickerComponent, HdDotPickerOption } from './hd-dot-picker.component';

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Compact kleurveld voor momentkleuren (spec docs/LEVENSTIJDLIJN.md §2.2):
 * de negen dusk-presets uit het notitiepalet (via de gedeelde
 * {@link HdDotPickerComponent}) plus een custom-optie met native colorpicker
 * en hex-invoer. Waarde = preset-key ('red'…'dusk') of '#rrggbb'; null = geen
 * kleur. Klik op de actieve keuze wist hem.
 */
@Component({
  selector: 'hd-color-picker',
  standalone: true,
  imports: [TranslocoModule, HdDotPickerComponent],
  template: `
    <div class="hd-color-picker">
      <hd-dot-picker
        [options]="presetOptions"
        [value]="presetValue()"
        (valueChange)="onPresetPicked($event)"
        testIdPrefix="color-swatch-"
      ></hd-dot-picker>
      <button
        type="button"
        class="hd-color-picker__swatch hd-color-picker__swatch--custom"
        [class.is-active]="customActive()"
        [style.background]="customActive() ? value() : null"
        [title]="'humanDesign.momentFields.color.custom' | transloco"
        (click)="toggleCustom()"
        data-test-id="color-swatch-custom"
      >
        @if (customActive()) {
          <i class="fas fa-check" aria-hidden="true"></i>
        } @else {
          <i class="fas fa-eye-dropper" aria-hidden="true"></i>
        }
      </button>
      <button
        type="button"
        class="hd-color-picker__clear"
        [disabled]="value() == null"
        [title]="'humanDesign.momentFields.color.none' | transloco"
        (click)="clear()"
        data-test-id="color-clear"
      >
        <i class="fas fa-ban" aria-hidden="true"></i>
      </button>
    </div>
    @if (customOpen()) {
      <div class="hd-color-picker__custom" data-test-id="color-custom-row">
        <input
          type="color"
          [value]="customHex()"
          (input)="onCustomPicked($any($event.target).value)"
          data-test-id="color-custom-input"
        />
        <input
          type="text"
          class="hd-color-picker__hex"
          maxlength="7"
          placeholder="#rrggbb"
          [value]="hexDraft()"
          (input)="onHexTyped($any($event.target).value)"
          data-test-id="color-hex-input"
        />
      </div>
    }
  `,
  styles: `
    .hd-color-picker {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    /* Custom-swatch: zelfde maatvoering en selectiestaat als de dot-picker. */
    .hd-color-picker__swatch {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border: 1px solid var(--separator);
      border-radius: 50%;
      cursor: pointer;
      color: #fff;
      font-size: 0.62rem;

      &.is-active {
        outline: 2px solid var(--dark-middle, #555);
        outline-offset: 2px;
      }

      i {
        text-shadow: 0 0 2px rgba(0, 0, 0, 0.6);
      }
    }

    .hd-color-picker__swatch--custom {
      background: var(--content-surface);
      color: var(--primary-75);
      font-size: 0.7rem;

      &.is-active {
        color: #fff;
      }
    }

    .hd-color-picker__clear {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      padding: 0;
      border: none;
      background: transparent;
      color: var(--primary-75);
      cursor: pointer;

      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }

    .hd-color-picker__custom {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 6px;

      input[type='color'] {
        width: 34px;
        height: 26px;
        padding: 0;
        border: 1px solid var(--separator);
        border-radius: var(--radius-md);
        background: var(--content-surface);
        cursor: pointer;
      }
    }

    .hd-color-picker__hex {
      width: 90px;
      padding: 3px 8px;
      border: 1px solid var(--separator);
      border-radius: var(--radius-md);
      background: var(--content-surface);
      font: inherit;
      font-size: 0.8rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdColorPickerComponent {
  /** Preset-key ('red'…'dusk') of '#rrggbb'; null = geen kleur. */
  readonly value = model<string | null>(null);

  /** De negen dusk-presets als gedeelde dot-picker-opties. */
  protected readonly presetOptions: readonly HdDotPickerOption[] = NOTE_COLOR_SWATCHES
    .map(swatch => ({ id: swatch.key, color: swatch.hex, label: swatch.key }));

  /** Custom-rij open (blijft open tijdens het kiezen, ook vóór er een geldige hex staat). */
  protected readonly customOpen = signal(false);
  /** Ruwe hex-invoer; pas een geldige "#rrggbb" belandt in het model. */
  protected readonly hexDraft = signal('');

  protected readonly customActive = computed(() => this.value()?.startsWith('#') ?? false);

  /** Actieve preset-key voor de dot-picker; een custom hex activeert geen preset-bolletje. */
  protected readonly presetValue = computed(() => {
    const value = this.value();
    return value != null && !value.startsWith('#') ? value : null;
  });

  /** Startwaarde van de native picker: de huidige custom kleur, anders amber. */
  protected readonly customHex = computed(() => {
    const value = this.value();
    return value?.startsWith('#') ? value : '#d4a869';
  });

  protected onPresetPicked(key: string | null): void {
    this.customOpen.set(false);
    this.value.set(key);
  }

  protected toggleCustom(): void {
    const open = !this.customOpen();
    this.customOpen.set(open);
    if (open) this.hexDraft.set(this.customActive() ? this.value()! : '');
  }

  protected clear(): void {
    this.customOpen.set(false);
    this.hexDraft.set('');
    this.value.set(null);
  }

  protected onCustomPicked(hex: string): void {
    if (!HEX_PATTERN.test(hex)) return;
    this.hexDraft.set(hex);
    this.value.set(hex);
  }

  protected onHexTyped(raw: string): void {
    const draft = raw.startsWith('#') || raw === '' ? raw : `#${raw}`;
    this.hexDraft.set(draft);
    if (HEX_PATTERN.test(draft)) this.value.set(draft);
  }
}
